import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import Hero from 'components/hero/SpecialOffersHero';
import SectionTitle from 'components/text/SectionTitle';
import { useTranslation } from 'next-i18next';
import axios from 'axios';
import { NextSeo } from 'next-seo';
import ReservationSection from 'components/reservation/ReservationSection';

export type SpecialOffers = {
  id: number;
  attributes: {
    title: string;
    Typ: string;
    locale: string;
    localizations: {
      data: [
        {
          attributes: {
            locale: string;
            title: string;
            Typ: string;
          };
        }
      ];
    };
  };
};

export type SnacksList = {
  id: number;
  attributes: {
    title: string;
    Cena: string;
    locale: string;
    localizations: {
      data: [
        {
          attributes: {
            locale: string;
            title: string;
            Cena: string;
          };
        }
      ];
    };
  };
};

type Props = {
  specialOffers: Array<SpecialOffers>;
  snacksList: Array<SnacksList>;
};
const SpecialOffers = ({ specialOffers, snacksList }: any) => {
  const { t, i18n } = useTranslation('specialOffers');
  return (
    <>
      <NextSeo
        title={t('meta.title') || ''}
        description={t('meta.desc') || ''}
        openGraph={{ title: t('meta.title') || '', description: t('meta.desc') || '' }}
      />
      <Hero></Hero>
      <section>
        <div className='container mx-auto px-4 flex flex-col'>
          <SectionTitle text={t('specialOffers.title')}></SectionTitle>
          <p className='mb-8'>{t('specialOffers.text')}</p>
          <h3 className='text-yellow font-bold text-2xl mb-4'>{t('specialOffers.standardRoom')}</h3>
          <div>
            {specialOffers &&
              specialOffers[i18n.language as any].standard.map((offer: any) => (
                <p key={offer.id} className='mb-4 last:mb-20'>
                  {offer.text}
                </p>
              ))}
          </div>
          <h3 className='text-yellow font-bold text-2xl mb-4'>{t('specialOffers.vipRoom')}</h3>
          <div>
            {specialOffers &&
              specialOffers[i18n.language as any].vip.map((offer: any) => (
                <p key={offer.id} className='mb-4 last:mb-20'>
                  {offer.text}
                </p>
              ))}
          </div>
          <SectionTitle text={t('snacks.title')}></SectionTitle>
          <p className='mb-8'>{t('snacks.text')}</p>
          <div className='flex justify-between mb-10'>
            <h3 className='text-yellow font-semibold text-2xl'>{t('snacks.offer')}</h3>
            <h3 className='text-yellow font-semibold text-2xl text-right'>{t('snacks.price')}</h3>
          </div>
          {snacksList &&
            snacksList[i18n.language as any].map((snackObject: any) => (
              <div className='flex justify-between mb-6 last:mb-20 border-b pb-2' key={snackObject.id}>
                <span className=''>{snackObject.text}</span>
                <span className='text-right'>{snackObject.price}</span>
              </div>
            ))}
        </div>
      </section>
      <ReservationSection></ReservationSection>
    </>
  );
};

export default SpecialOffers;

export async function getStaticProps({ locale }: any) {
  let specialOffers;
  let snacksList;
  try {
    const specialoffersStandardCSResponse = await axios.get(`${process.env.API_URL}/api/akcni-nabidky-standard?populate=*&locale=cs`, {
      headers: { Authorization: `Bearer ${process.env.STRAPI_TOKEN}` },
    });
    const specialoffersStandardCS = specialoffersStandardCSResponse.data.data.attributes.specialOffers;

    const specialoffersStandardENResponse = await axios.get(`${process.env.API_URL}/api/akcni-nabidky-standard?populate=*&locale=en`, {
      headers: { Authorization: `Bearer ${process.env.STRAPI_TOKEN}` },
    });
    const specialoffersStandardEN = specialoffersStandardENResponse.data.data.attributes.specialOffers;

    const specialoffersVIPCSResponse = await axios.get(`${process.env.API_URL}/api/akcni-nabidky-vip?populate=*&locale=cs`, {
      headers: { Authorization: `Bearer ${process.env.STRAPI_TOKEN}` },
    });
    const specialoffersVIPCS = specialoffersVIPCSResponse.data.data.attributes.specialOffers;

    const specialoffersVIPENResponse = await axios.get(`${process.env.API_URL}/api/akcni-nabidky-vip?populate=*&locale=en`, {
      headers: { Authorization: `Bearer ${process.env.STRAPI_TOKEN}` },
    });
    const specialoffersVIPEN = specialoffersVIPENResponse.data.data.attributes.specialOffers;

    const snacksCSResponse = await axios.get(`${process.env.API_URL}/api/obcerstveni2?populate=*&locale=cs`, {
      headers: { Authorization: `Bearer ${process.env.STRAPI_TOKEN}` },
    });
    const snacksCS = snacksCSResponse.data.data.attributes.snacks;

    const snacksENResponse = await axios.get(`${process.env.API_URL}/api/obcerstveni2?populate=*&locale=en`, {
      headers: { Authorization: `Bearer ${process.env.STRAPI_TOKEN}` },
    });
    const snacksEN = snacksENResponse.data.data.attributes.snacks;

    specialOffers = {
      cs: {
        standard: specialoffersStandardCS,
        vip: specialoffersVIPCS,
      },
      en: {
        standard: specialoffersStandardEN,
        vip: specialoffersVIPEN,
      },
    };

    snacksList = {
      cs: snacksCS,
      en: snacksEN,
    };
  } catch (error) {
    console.error(error);
  }
  return {
    props: {
      specialOffers,
      snacksList,
      ...(await serverSideTranslations(locale, ['common', 'specialOffers'])),
    },
    revalidate: 300,
  };
}
